import type { TrpcClient } from "@common/api-interface/trpc-client";
import HandledErrors, { HandledError } from "@common/consts/errors";
import type { RealtimeService } from "@common/services/realtime/realtime-service";
import type { SpaceId } from "@common/types/space";
import type { Http } from "@common/utils/http";
import type { HashtagMeta } from "@web/src/api/model/hashtag-model";
import EventEmitter from "eventemitter3";
import _ from "lodash";
import type {
  DeleteTodoNoteRequest,
  DeleteTodoNoteResponse,
  GetHistoricalTodosRequest,
  GetHistoricalTodosResponse,
  GetTodoNoteRequest,
  GetTodoNoteResponse,
  GetTodosRequest,
  GetTodosResponse,
  MigrateSpaceRequest,
  MigrateSpaceResponse,
  UpdateSpaceTodosRequestV2,
  UpdateSpaceTodosResponseV2,
  UpdateTodoNoteRequest,
  UpdateTodoNoteResponse,
} from "../api-interface/todos";
import type { UpdateNotePayload } from "../types/notes";
import type {
  Todo,
  TodoId,
  TodoIdMap,
  TodoPayloadIdMap,
  TodoPayloadItem,
  TodoUpdateDelta,
  TodoUpdateDeltaIdMap,
} from "../types/todo";
import { CommonEvents } from "./common-events";

export type TodoService = ReturnType<typeof makeTodoService>;

export type TodoServiceEventTypes = {
  ON_TODO_UPDATE_SUCCESS: (params: { todoId: TodoId }) => void;
  ON_TODO_UPDATE_FAILED: (params: { todoId: TodoId; error: string }) => void;
};

export const makeTodoService = ({
  Http,
  trpc,
  realtimeService,
}: {
  Http: Http;
  trpc: TrpcClient;
  realtimeService: RealtimeService;
}) => {
  /**
   * Differentiate between todos that need to be created
   * and todo that need to be updated
   */
  let _updateStash: {
    [spaceId: SpaceId]: TodoPayloadIdMap;
  } = {};

  function pushCreate(todo: Todo, spaceId: SpaceId) {
    if (!_updateStash[spaceId]) {
      _updateStash[spaceId] = {};
    }

    _updateStash[spaceId]![todo.id] = {
      type: "CREATE",
      data: todo,
      date: performance.now(),
    };
  }

  function pushUpdate(delta: TodoUpdateDelta, spaceId: SpaceId) {
    if (!_updateStash[spaceId]) {
      _updateStash[spaceId] = {};
    }

    const currentUpdate = _updateStash[spaceId]![delta.id];

    // merge the updates
    _updateStash[spaceId]![delta.id] = mergeUpdates(currentUpdate, {
      type: "UPDATE",
      data: delta,
      date: performance.now(),
    });
  }

  let todoService = {
    getTodos,
    getTodosV2,
    getHistoricalTodos,
    getNoteForTodo,
    updateNoteForTodo,
    deleteNoteForTodo,
    createTodo,
    updateTodos,
    migrateTodos,
    dispatchUpdates,
    eventEmitter: new EventEmitter<TodoServiceEventTypes>(),
    debouncedDispatch: _.debounce(dispatchUpdates, 1000), //send the actual API call every 1 second,
    debouncedUpdateNoteForTodo: _.debounce(
      (
        todoId: TodoId,
        note: UpdateNotePayload,
        onSave: () => void,
        onError: (error: string) => void
      ) => {
        updateNoteForTodo(todoId, note)
          .then(onSave)
          .catch((err) => {
            if (err instanceof Error) {
              onError(err.message);
            } else if (typeof err === "string") {
              onError(err);
            } else {
              onError("Unknown error");
            }
          });
      },
      1000
    ), //send the actual API call every 1 second
    upsertHashtagMetas,
    deleteHashtagMeta,
    getHashtagsForSpace,
  };

  return todoService;

  async function getTodos(
    spaceId: SpaceId,
    timezone: string
  ): Promise<TodoIdMap | undefined> {
    let res = await Http.json<GetTodosRequest, GetTodosResponse>(
      "GET",
      `/v1/spaces/${spaceId}/todos`,
      { timezone }
    );

    if (res) {
      return res.todoIdMap;
    }

    throw new Error("Malformed response");
  }

  async function getTodosV2(
    spaceId: SpaceId,
    filter?: Partial<Todo>,
    dateRange?: { before: number; after: number }
  ) {
    const todoIdMap = await trpc.todos.getTodos.query({
      spaceId,
      dateRange,
      filter,
    });
    return todoIdMap;
  }

  async function getHistoricalTodos(
    spaceId: string,
    timezone: string
  ): Promise<TodoIdMap> {
    try {
      let res = await Http.json<
        GetHistoricalTodosRequest,
        GetHistoricalTodosResponse
      >("GET", `/v1/spaces/${spaceId}/historical`, { timezone });

      if (res) {
        return res.todoIdMap ?? {};
      }

      throw new Error("Malformed response");
    } catch (error) {
      console.error("Retrieve todos failed");
      console.error(error);
      return {};
    }
  }

  async function createTodo(
    todo: Todo,
    spaceId: SpaceId,
    { immediate } = { immediate: false }
  ) {
    pushCreate(todo, spaceId);

    if (immediate) {
      await todoService.dispatchUpdates();
      return;
    }

    todoService.debouncedDispatch();
  }

  async function updateTodos(
    deltaIdMap: TodoUpdateDeltaIdMap,
    spaceId: SpaceId,
    { immediate } = { immediate: false }
  ): Promise<void> {
    Object.entries(deltaIdMap).forEach(([todoId, todoDelta]) => {
      pushUpdate(todoDelta, spaceId);
    });

    if (immediate) {
      await todoService.dispatchUpdates();
      return;
    }

    todoService.debouncedDispatch();
  }

  async function dispatchUpdates() {
    await Promise.all(
      Object.keys(_updateStash).map(async (spaceId) => {
        const originalUpdateIdMap = _.cloneDeep(_updateStash[spaceId]!);

        _updateStash[spaceId] = {};

        try {
          if (Object.keys(originalUpdateIdMap).length === 0) return; // no need to update if there's nothing to update

          let res = await Http.json<
            UpdateSpaceTodosRequestV2,
            UpdateSpaceTodosResponseV2
          >("POST", `/v2/spaces/${spaceId}/todos`, {
            payload: originalUpdateIdMap,
          });

          // Once request is successful, we consider all of them to exist on the server
          for (const todoId of Object.keys(originalUpdateIdMap)) {
            todoService.eventEmitter.emit("ON_TODO_UPDATE_SUCCESS", { todoId });
          }

          const { failed } = res;
          for (const failedUpdate of failed) {
            const { todoId, error } = failedUpdate;
            todoService.eventEmitter.emit("ON_TODO_UPDATE_FAILED", {
              todoId,
              error,
            });
          }

          for (let failedUpdate of failed) {
            const { todoId, error, success } = failedUpdate;

            console.info("Failed to update todo", todoId, error);

            if (success) continue;

            const originalUpdateTodo = originalUpdateIdMap[todoId];
            if (!originalUpdateTodo) {
              console.error(
                "Update failed, and original todo is nowhere to be found"
              );
              continue;
            }

            // recover the todo
            _updateStash[spaceId]![todoId] = mergeUpdates(
              originalUpdateTodo,
              _updateStash[spaceId]![todoId]
            );

            CommonEvents.emit("PUBLISH_TOAST", {
              title:
                "Failed to save todo: " +
                (originalUpdateTodo.data.value ?? originalUpdateTodo.data.id),
              content: error ?? "Unknown error",
              titleColor: "negative",
            });
          }
        } catch (error) {
          if (
            error instanceof HandledError &&
            error.isError(HandledErrors.PRO_PLAN_REQUIRED)
          ) {
            CommonEvents.emit("PAYWALL", { from: "create-todo" });
            // IMPORTANT!!! we let the updates go through so that user's data isn't lost
            // so that we can clear the queue (which would have otherwise been re-merged)
            // resulting in an endless paywall loop
            return;
          }

          for (let todoId in originalUpdateIdMap) {
            _updateStash[spaceId]![todoId] = mergeUpdates(
              originalUpdateIdMap[todoId],
              _updateStash[spaceId]![todoId]
            );
          }

          CommonEvents.emit("PUBLISH_TOAST", {
            title: "Failed to save todos",
            content: (error as Error).message || "Please try again later",
            titleColor: "negative",
          });
        }
      })
    );
  }

  async function getNoteForTodo(todoId: TodoId) {
    try {
      const { note } = await Http.json<GetTodoNoteRequest, GetTodoNoteResponse>(
        "GET",
        `/v1/todos/${todoId}/notes`
      );

      return note ?? null;
    } catch (error) {
      console.error(error);
      return null;
    }
  }
  async function updateNoteForTodo(todoId: TodoId, note: UpdateNotePayload) {
    const res = await Http.json<UpdateTodoNoteRequest, UpdateTodoNoteResponse>(
      "POST",
      `/v1/todos/${todoId}/notes`,
      { note: note }
    );
    if (!res.success) throw new Error(`Update notes failed for todo ${todoId}`);
    return { created_id: res.created_id };
  }

  async function deleteNoteForTodo(
    todoId: TodoId
  ): Promise<{ success: boolean }> {
    try {
      const { success } = await Http.json<
        DeleteTodoNoteRequest,
        DeleteTodoNoteResponse
      >("DELETE", `/v1/todos/${todoId}/notes`);
      if (!success) console.warn("delete notes failed");
      return { success: true };
    } catch (error) {
      console.error(error);
      return { success: false };
    }
  }

  // rarely we allow updating of space
  // this is the one place we allow it
  async function migrateTodos(
    todoIds: TodoId[],
    fromSpaceId: SpaceId,
    toSpaceId: SpaceId
  ) {
    // flush, because the todos will be changing spaces
    await dispatchUpdates();
    const { success } = await Http.json<
      MigrateSpaceRequest,
      MigrateSpaceResponse
    >("POST", `/v1/spaces/${fromSpaceId}/todos/migrate`, {
      toSpaceId,
      todoIds,
    });

    if (!success) console.warn("migrate todos failed");
    return success;
  }

  async function deleteHashtagMeta(
    hashtag: string,
    spaceId: SpaceId
  ): Promise<void> {
    await trpc.todos.deleteHashtagMeta.mutate({ hashtag, spaceId });
  }

  async function upsertHashtagMetas(
    hashtagMetas: HashtagMeta[]
  ): Promise<void> {
    await trpc.todos.upsertHashtagMetas.mutate(hashtagMetas);
  }

  async function getHashtagsForSpace(spaceId: SpaceId): Promise<HashtagMeta[]> {
    const response = await trpc.todos.getHashtagsForSpace.query({ spaceId });
    return response;
  }
};

export function mergeUpdates(
  ...args: (TodoPayloadItem | undefined)[]
): TodoPayloadItem {
  const updates = args.filter(
    (item): item is TodoPayloadItem => item !== undefined
  );

  // sort updates by date
  updates.sort((a, b) => a.date - b.date);

  const earliestUpdate = updates[0];
  if (!earliestUpdate) throw new Error("No updates provided");

  if (earliestUpdate.type === "CREATE") {
    let data = earliestUpdate.data;
    let date = earliestUpdate.date;

    for (let payloadItem of updates) {
      data = { ...data, ...payloadItem.data };
      date = payloadItem.date;
    }

    return { type: "CREATE", data, date };
  }

  // type = UPDATE
  // if we find a CREATE todo, it's probably a mistake
  // since CREATE should always come first
  // but we still recover by treating it as a CREATE
  let type = "UPDATE";
  let data = earliestUpdate.data;
  let date = earliestUpdate.date;

  for (let payloadItem of updates) {
    if (payloadItem.type === "CREATE") {
      console.warn(
        "Payload of type CREATE inserted after UPDATE. This is not expected behavior"
      );
      type = payloadItem.type;
      data = { ...data, ...payloadItem.data };
      date = payloadItem.date;
    }
    data = { ...data, ...payloadItem.data };
    date = payloadItem.date;
  }

  return { type: "UPDATE", data, date };
}
